import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { apiError } from "@/lib/api-error";
import type { TFunction } from "@/i18n/catalog";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** A signed read URL for a brand asset, shared by everything that needs to look at the logo.
 *
 * One definition rather than two because the `staleTime` is a constraint rather than a preference:
 * the signed URL lives an hour (`r2_images.py`, `READ_URL_TTL_SECONDS`), so holding it for half
 * that keeps an `<img src>` stable across renders while never handing out one that has expired. A
 * second copy of that reasoning is a second thing to keep correct.
 *
 * The query key is what makes the colour sampler free on both surfaces (POS2-1149): the logo
 * preview beside it has already populated this entry for the same key.
 */
export function useLogoReadUrl(t: TFunction, logoKey: string | null) {
  const headers = useApiHeaders();
  return useQuery({
    queryKey: ["images", "read-url", logoKey],
    enabled: !!logoKey,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      // `enabled` above already guarantees a key. Asserted rather than written as `logoKey!`, so a
      // caller that drops `enabled` fails loudly instead of fetching `?key=null`.
      if (!logoKey) throw new Error("useLogoReadUrl ran without a logo key");
      const res = await fetch(
        `${API_URL}/api/v1/images/read-url?key=${encodeURIComponent(logoKey)}`,
        { headers },
      );
      if (!res.ok) throw await apiError(t, res);
      const json = await res.json();
      return json.data.url as string;
    },
  });
}
